import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Phone, ShieldCheck } from 'lucide-react';
import { Button } from '@dreamtree-org/twreact-ui';
import { useCompany } from '@site/content';

const ease = [0.22, 1, 0.36, 1];

/** Full-bleed photo hero with staggered text reveal (pattern from 21st.dev hero sections). */
export default function HeroSection({ hero }) {
  const company = useCompany();
  const reduce = useReducedMotion();
  const item = (i) =>
    reduce ? {} : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: 0.1 + i * 0.12, ease } };

  return (
    <section className="relative isolate overflow-hidden bg-ink-950">
      <motion.img
        src={hero.image}
        alt={hero.imageAlt}
        fetchpriority="high"
        className="absolute inset-0 -z-20 h-full w-full object-cover object-[30%_center]"
        {...(reduce ? {} : { initial: { scale: 1.08 }, animate: { scale: 1 }, transition: { duration: 2.4, ease } })}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-ink-950/95 via-ink-950/70 to-ink-950/10" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-40 bg-gradient-to-t from-ink-950/80 to-transparent" />

      <div className="site-container flex min-h-[calc(100svh-4.5rem)] flex-col justify-center py-20 sm:min-h-[44rem] md:min-h-[calc(100svh-7rem)]">
        <div className="max-w-3xl">
          <motion.p {...item(0)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-accent-300 backdrop-blur">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            {hero.eyebrow}
          </motion.p>
          <motion.h1 {...item(1)} className="mt-6 text-balance text-5xl leading-[0.95] text-white sm:text-7xl lg:text-8xl">
            {hero.titleStart}{' '}
            <span className="bg-flame bg-clip-text text-transparent">{hero.titleHighlight}</span>
          </motion.h1>
          <motion.p {...item(2)} className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-ink-200">
            {hero.text}
          </motion.p>
          <motion.div {...item(3)} className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button as={Link} to="/contact" size="lg" className="!bg-primary-600 hover:!bg-primary-500" rightIcon={<ArrowRight className="h-5 w-5" />}>
              {hero.primaryLabel}
            </Button>
            <Button as={Link} to="/services" size="lg" variant="outline" className="!border-white/30 !text-white hover:!bg-white/10">
              {hero.secondaryLabel}
            </Button>
            <a href={company.phoneHref} className="inline-flex items-center gap-2 px-2 py-3 text-sm font-medium text-white/90 hover:text-white">
              <Phone className="h-4 w-4 text-accent-400" aria-hidden="true" /> {company.phone}
            </a>
          </motion.div>
        </div>

        <motion.div
          {...item(4)}
          className="mt-12 flex w-fit items-center gap-4 rounded-2xl border border-white/10 bg-white/10 p-4 pr-6 backdrop-blur-md lg:absolute lg:bottom-16 lg:right-8 lg:mt-0"
        >
          <span className="font-display text-5xl font-extrabold uppercase leading-none text-accent-400">{hero.badgeValue}</span>
          <span className="max-w-[11rem] text-sm leading-snug text-white">{hero.badgeLabel}</span>
        </motion.div>
      </div>
    </section>
  );
}
